import { useRouter } from 'next/router';
import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { RootStores } from '@portal/env/stores/RootStores';
import { useMutation } from '@tanstack/react-query';
import { mutationKeys } from '@portal/ui/hooks/queryKeys';
import { usePaymentRoutes } from '@portal/env/hooks/payments/usePaymentRoutes';
import type { InvoiceSummary } from '@bw-ui/basket';
import type {
  InvoicePreview,
  InvoicePreviewError,
} from '@portal/types/nexudus';

type InvoicePreviewResult =
  | (InvoicePreview & Partial<InvoicePreviewError>)
  | null;

/**
 * True when the preview loaded cleanly and priced the whole basket at £0
 * (booking credits, a 100% team discount or a 100% code). Only then is an
 * empty CreateInvoice response expected. A missing or failed preview (Nexudus
 * rejections always carry a Message) is never treated as free, even though the
 * summary falls back to all-zero figures.
 */
const previewPricesBasketAtZero = (preview?: InvoicePreviewResult) =>
  !!preview &&
  !preview.Message &&
  preview.TotalAmount != null &&
  preview.TotalAmount < 0.005;

export const useCreateBasketInvoice = ({
  proposalId,
  invoiceSummary,
  invoicePreview,
}: {
  proposalId?: number;
  returnUrl?: string;
  completeUrl?: string;
  invoiceSummary?: InvoiceSummary;
  invoicePreview?: InvoicePreviewResult;
}) => {
  const stores = useContext(MobXProviderContext) as RootStores;

  if (!stores?.checkoutStore || !stores?.appStore || !stores?.bookingsStore) {
    throw new Error('useBasket must be used within a MobX Provider');
  }

  const { checkoutStore, appStore, bookingsStore } = stores;

  const routes = usePaymentRoutes();
  const router = useRouter();

  return useMutation({
    mutationKey: [...mutationKeys.payments, 'createBasketInvoice'],
    mutationFn: async () => {
      const checkoutBasket = checkoutStore.getBasketForCheckout();
      if (checkoutBasket.length === 0) {
        await appStore.setPopMessage(
          'Sorry, none of the selected tickets are available. Please check availability and try again.',
        );
        return;
      }

      const invoice = await checkoutStore.loadBasketInvoice({
        ...(proposalId ? { proposalId } : {}),
        createZeroValueInvoice: true,
      });

      // Business-logic rejection from Nexudus (e.g. sold out): surface the message.
      // Only WasSuccessful === false is a reliable rejection signal. Message alone
      // is informational and is also present on successful credit-covered invoices.
      if (invoice?.WasSuccessful === false) {
        const msg =
          invoice?.Message ??
          'Sorry, we could not process this transaction. Check the items in your basket are still available.';
        await appStore.setPopMessage(msg);
        return;
      }

      // Nexudus answers CreateInvoice with an empty 200 when nothing is owed —
      // e.g. booking credits cover the basket — yet it has still created the
      // bookings and drawn the credits. Treat that as success only when the
      // preview priced the basket at £0; otherwise an empty response is an
      // error. A £0 total means no account credit was drawn (BCD-1738 decision 3).
      if (!invoice && previewPricesBasketAtZero(invoicePreview)) {
        const basketKey = checkoutStore.savePastBasket(
          invoiceSummary
            ? { ...invoiceSummary, accountCredits: 0, amountDue: 0 }
            : invoiceSummary,
        );
        await router.push(`${routes.checkout_complete}?key=${basketKey}`);
        checkoutStore.clearBasket();
        return;
      }

      // Null or malformed response — basket items produced no invoice.
      if (!invoice?.Id) {
        await appStore.setPopMessage(
          'Sorry, we could not process this transaction. Check the items in your basket are still available.',
        );
        return;
      }

      if (invoice.DueAmount === 0) {
        // Zero due: real invoice exists, fully settled by account credits or free.
        //
        // The two look different in the breakdown (BCD-1738 decision 3):
        // booking credits and discounts zero the TotalAmount itself and net
        // out the VAT, so a £0 total means no account credit was drawn. A
        // non-zero total with nothing due was settled by account credits,
        // which apply after VAT against the gross total — so the VAT and
        // Total stay visible, with the credit shown between Total and Amount
        // due. Prefer the real invoice figures over the preview summary.
        const round2 = (n: number) => Math.round(n * 100) / 100;
        const total = invoice.TotalAmount ?? invoiceSummary?.total ?? 0;
        const tax = invoice.TaxAmount ?? invoiceSummary?.tax ?? 0;
        const accountCredits = Math.max(
          0,
          round2(total - (invoice.DueAmount ?? 0)),
        );
        const summaryWithCredits: InvoiceSummary | undefined = invoiceSummary
          ? {
              ...invoiceSummary,
              total,
              tax,
              accountCredits,
              amountDue: 0,
            }
          : invoiceSummary;
        const basketKey = checkoutStore.savePastBasket(summaryWithCredits);
        await router.push(`${routes.checkout_complete}?key=${basketKey}`);
        checkoutStore.clearBasket();
        return;
      }

      // Success! Navigate first, then clear the basket so the page doesn't
      // flash the empty-basket state while router.push is in progress.
      await router.push(routes.checkout_payment_invoice(invoice.Id));
      checkoutStore.clearBasket();
      // We need to refresh the unpaid bookings, but that can happen after we navigate away - it doesn't need to block the navigation
      await bookingsStore.loadUnpaidBookings();
      return invoice;
    },
    onError: async () => {
      await appStore.setPopMessage(
        'Sorry, we could not process this transaction.',
      );
    },
  });
};
